import { SwitchRow } from '@components'

import { Text } from '@billy/design-system'
import { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

import { useFormContext } from '../../../../../../../../hooks'
import type { InvoicingSettingsSection } from '../../../../../../enums/invoicingSettingsSection'
import { useTrackInvoicingSettings } from '../../../../../../hooks/useTrackInvoicingSettings'
import type { LogoAndBrandingSettingsFormSchema } from '../../../../types/logoAndBrandingSettingsFormSchema'

interface ApplyColorToDocumentElementsProps {
  trackingSection?: InvoicingSettingsSection
}

export const ApplyColorToDocumentElements = ({ trackingSection }: ApplyColorToDocumentElementsProps): ReactElement => {
  const { t } = useTranslation()
  const { trackInvoicingSettings } = useTrackInvoicingSettings({ section: trackingSection })
  const { FormItem } = useFormContext<LogoAndBrandingSettingsFormSchema>()

  const columnHeadersLabel = t(
    'settings.organization.external_invoice.appearance.apply_color.switch_label.column_headers',
  )
  const dividersLabel = t('settings.organization.external_invoice.appearance.apply_color.switch_label.dividers')
  const headerLabel = t('settings.organization.external_invoice.appearance.apply_color.switch_label.header')
  const totalLabel = t('settings.organization.external_invoice.appearance.apply_color.switch_label.total')

  return (
    <>
      <Text colorVariant="placeholder">{t('settings.organization.external_invoice.appearance.apply_color.title')}</Text>
      <FormItem
        name="shouldApplyTemplateColorToDividers"
        render={({ field: { onChange, value, ...props } }) => (
          <SwitchRow
            {...props}
            aria-label={dividersLabel}
            checked={value}
            label={dividersLabel}
            onChange={(event) => {
              onChange(event)
              trackInvoicingSettings('Color applied to Table lines', { checked: event.target.checked })
            }}
          />
        )}
      />
      <FormItem
        name="shouldApplyTemplateColorToTotal"
        render={({ field: { onChange, value, ...props } }) => (
          <SwitchRow
            {...props}
            aria-label={totalLabel}
            checked={value}
            label={totalLabel}
            onChange={(event) => {
              onChange(event)
              trackInvoicingSettings('Color applied to Total due', { checked: event.target.checked })
            }}
          />
        )}
      />
      <FormItem
        name="shouldApplyTemplateColorToColumnHeaders"
        render={({ field: { onChange, value, ...props } }) => (
          <SwitchRow
            {...props}
            aria-label={columnHeadersLabel}
            checked={value}
            label={columnHeadersLabel}
            onChange={(event) => {
              onChange(event)
              trackInvoicingSettings('Color applied to Column headers', { checked: event.target.checked })
            }}
          />
        )}
      />
      <FormItem
        name="shouldApplyTemplateColorToHeader"
        render={({ field: { onChange, value, ...props } }) => (
          <SwitchRow
            {...props}
            aria-label={headerLabel}
            bordered={false}
            checked={value}
            label={headerLabel}
            onChange={(event) => {
              onChange(event)
              trackInvoicingSettings('Color applied to Document header', { checked: event.target.checked })
            }}
          />
        )}
      />
    </>
  )
}
